import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  CreditCard,
  CalendarDays,
  AlertTriangle,
  Clock,
  Users,
  DollarSign,
  IdCard,
} from 'lucide-react';
import { DashboardStats } from '../../types';
import HelpTooltip from '../common/HelpTooltip';
import { cn } from '../../lib/utils';

interface StatsCardsProps {
  stats: DashboardStats;
}

// Animated counter hook
function useAnimatedCount(target: number, duration = 800) {
  const [count, setCount] = useState(0);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (target === 0) { setCount(0); return; }
    let start: number | null = null;
    const animate = (ts: number) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
      else setCount(target);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);

  return count;
}

const topRowStats = [
  {
    key: 'totalClients',
    name: 'Total Clients',
    icon: Users,
    iconBg: 'bg-indigo-500/10 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-400',
    link: '/clients',
    help: 'Total number of clients in your Pax8 account. Click to view and manage all clients.',
  },
  {
    key: 'totalLicenses',
    name: 'Total Licenses',
    icon: IdCard,
    iconBg: 'bg-cyan-500/10 text-cyan-600 dark:bg-cyan-500/20 dark:text-cyan-400',
    link: '/subscriptions',
    help: 'Total number of licenses across all active subscriptions.',
  },
  {
    key: 'monthlyRecurringRevenue',
    name: 'Monthly Revenue',
    icon: DollarSign,
    iconBg: 'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400',
    link: '/reports',
    format: 'currency',
    help: 'Estimated monthly recurring revenue from all active subscriptions.',
  },
  {
    key: 'totalActiveSubscriptions',
    name: 'Active Subscriptions',
    icon: CreditCard,
    iconBg: 'bg-blue-500/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400',
    link: '/subscriptions',
    help: 'Number of active subscriptions being managed.',
  },
];

const bottomRowStats = [
  {
    key: 'renewalsNext30Days',
    name: 'Renewals (30 days)',
    icon: CalendarDays,
    iconBg: 'bg-amber-500/10 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400',
    link: '/subscriptions?renewalDays=30',
    help: 'Subscriptions renewing in the next 30 days.',
  },
  {
    key: 'atRiskClientsCount',
    name: 'At-Risk Clients',
    icon: AlertTriangle,
    iconBg: 'bg-red-500/10 text-red-600 dark:bg-red-500/20 dark:text-red-400',
    link: '/clients?status=at-risk',
    highlight: true,
    help: 'Clients marked as at-risk. These need immediate attention.',
  },
  {
    key: 'pendingCancellations',
    name: 'Pending Cancellations',
    icon: Clock,
    iconBg: 'bg-orange-500/10 text-orange-600 dark:bg-orange-500/20 dark:text-orange-400',
    link: '/subscriptions?status=pending-cancellation',
    help: 'Subscriptions with cancellation requests submitted but not yet verified.',
  },
];

function formatValue(value: number, format?: string): string {
  if (format === 'currency') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(value);
  }
  return new Intl.NumberFormat('en-US').format(value);
}

interface StatCardProps {
  stat: {
    key: string;
    name: string;
    icon: React.ComponentType<{ className?: string }>;
    iconBg: string;
    link: string;
    highlight?: boolean;
    format?: string;
    help?: string;
  };
  value: number;
  delay?: number;
}

function StatCard({ stat, value, delay = 0 }: StatCardProps) {
  const isHighlight = stat.highlight && value > 0;
  const animatedValue = useAnimatedCount(value);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  return (
    <Link
      to={stat.link}
      className={cn(
        "group relative rounded-lg border bg-card p-3 sm:p-4 transition-all duration-300 hover-lift block overflow-hidden",
        isHighlight && "ring-2 ring-red-500/50 border-red-500/30",
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
      )}
    >
      <div className="flex items-center justify-between">
        <div className={cn("flex-shrink-0 rounded-lg p-2.5", stat.iconBg)}>
          <stat.icon className="h-5 w-5" />
        </div>
        {isHighlight && (
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
          </span>
        )}
      </div>
      <div className="mt-3">
        <div className="flex items-center gap-1">
          <p className="text-xs font-medium text-muted-foreground truncate">
            {stat.name}
          </p>
          {stat.help && (
            <span onClick={(e) => e.preventDefault()}>
              <HelpTooltip text={stat.help} size="sm" />
            </span>
          )}
        </div>
        <p className={cn(
          "text-2xl font-bold mt-0.5 tabular-nums",
          isHighlight ? "text-red-600 dark:text-red-400" : "text-foreground"
        )}>
          {formatValue(animatedValue, stat.format)}
        </p>
      </div>
    </Link>
  );
}

export default function StatsCards({ stats }: StatsCardsProps) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {topRowStats.map((stat, i) => (
          <StatCard
            key={stat.key}
            stat={stat}
            value={stats[stat.key as keyof DashboardStats]}
            delay={i * 75}
          />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {bottomRowStats.map((stat, i) => (
          <StatCard
            key={stat.key}
            stat={stat}
            value={stats[stat.key as keyof DashboardStats]}
            delay={(i + 4) * 75}
          />
        ))}
      </div>
    </div>
  );
}
